import { ReactNode, useMemo } from "react";
import { useQuery } from "react-query";
import { BrandingContext } from "./model";
import { API } from "../../utils/AxiosInstance";
import { useProfile } from "../../ui/profile/queries";
import { LoadingContainer } from "../../ui/components/loading-container"; // Adjust the import based on your project structure

const APP_LABEL = import.meta.env.VITE_APP_LABEL || "HabitNu";
const APP_ABBREVIATION = import.meta.env.VITE_APP_ABBREVIATION || APP_LABEL;
const APP_LOGO_URL =
    import.meta.env.VITE_APP_LOGO_URL || "/images/habitnu/logo.png";
const APP_BACKGROUND_URL =
    import.meta.env.VITE_APP_BACKGROUND_URL || "/images/habitnu/background.jpg";
const APP_REGISTRATION_URL =
    import.meta.env.VITE_APP_REGISTRATION_URL ||
    "https://habitnu.com/signup-page";
const APP_CLIENT_ID = import.meta.env.VITE_APP_CLIENT_ID || null;
const APP_PRIVACY_POLICY_URL =
    import.meta.env.VITE_APP_PRIVACY_POLICY_URL || "/privacy-policy";
const APP_TERMS_OF_USE_URL =
    import.meta.env.VITE_APP_TERMS_OF_USE_URL || "/terms-of-use";
const VITE_APP_APP_STORE_URL = import.meta.env.VITE_APP_APP_STORE_URL || null;
const VITE_APP_PLAY_STORE_URL = import.meta.env.VITE_APP_PLAY_STORE_URL || null;
const VITE_APP_EULA_URL = import.meta.env.VITE_APP_EULA_URL || "";
const VITE_APP_PAYMENT_TC_URL = import.meta.env.VITE_APP_PAYMENT_TC_URL || "";
const VITE_APP_PAYMENT_FAQ_URL = import.meta.env.VITE_APP_PAYMENT_FAQ_URL || "";
const VITE_APP_PAYMENT_REFUND_POLICY =
    import.meta.env.VITE_APP_PAYMENT_REFUND_POLICY || "";

interface Props {
    children: ReactNode;
}

export function BrandingContextProvider({ children }: Props) {
    const isLoggedIn = !!localStorage.getItem("token");
    const { data: profile } = useProfile(!isLoggedIn);

    const { data: regionData, isLoading: regionLoading } = useQuery(
        "region",
        () =>
            API.get<{ region: string }>("/current_region", {
                headers: { Accept: "application/json" },
            }).then((res) => res.data.region),
        {
            enabled: !isLoggedIn,
        }
    );

    const countryCode =
        isLoggedIn && profile ? profile.countryCode : regionData;
    const value = useMemo(() => {
        const isIndia = countryCode === "IN";
        return {
            label: APP_LABEL,
            abbreviation: APP_ABBREVIATION,
            logoUrl: APP_LOGO_URL,
            backgroundUrl: APP_BACKGROUND_URL,
            registrationUrl: isIndia
                ? import.meta.env.VITE_APP_REGISTRATION_URL_INDIA ||
                  APP_REGISTRATION_URL
                : APP_REGISTRATION_URL,
            clientId: APP_CLIENT_ID,
            privacyUrl: isIndia
                ? import.meta.env.VITE_APP_PRIVACY_POLICY_URL_INDIA ||
                  APP_PRIVACY_POLICY_URL
                : APP_PRIVACY_POLICY_URL,
            termsUrl: isIndia
                ? import.meta.env.VITE_APP_TERMS_OF_USE_URL_INDIA ||
                  APP_TERMS_OF_USE_URL
                : APP_TERMS_OF_USE_URL,
            appStoreUrl: VITE_APP_APP_STORE_URL,
            playStoreUrl: VITE_APP_PLAY_STORE_URL,
            eulaUrl: isIndia
                ? import.meta.env.VITE_APP_EULA_URL_INDIA || VITE_APP_EULA_URL
                : VITE_APP_EULA_URL,
            paymentTC: isIndia
                ? import.meta.env.VITE_APP_PAYMENT_TC_URL_INDIA ||
                  VITE_APP_PAYMENT_TC_URL
                : VITE_APP_PAYMENT_TC_URL,
            paymentFAQ: isIndia
                ? import.meta.env.VITE_APP_PAYMENT_FAQ_URL_INDIA ||
                  VITE_APP_PAYMENT_FAQ_URL
                : VITE_APP_PAYMENT_FAQ_URL,
            refundPolicyUrl: isIndia
                ? import.meta.env.VITE_APP_PAYMENT_REFUND_POLICY_INDIA ||
                  VITE_APP_PAYMENT_REFUND_POLICY
                : VITE_APP_PAYMENT_REFUND_POLICY,
        };
    }, [countryCode]);

    if (regionLoading) {
        return <LoadingContainer />;
    }

    return (
        <BrandingContext.Provider value={value}>
            {children}
        </BrandingContext.Provider>
    );
}
